import { COPILOT_ID } from "@repo/app-types/entities/copilot";
import { ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { chatHeaderIconClassName } from "@repo/ui/chat/chatHeader";
import { SimpleMenu } from "@repo/ui/Menu/simpleMenu";
import { SimpleMenuItem } from "@repo/ui/Menu/simpleMenuItem";
import { SimpleMenuPopover } from "@repo/ui/Menu/simpleMenuPopover";
import {
  IconDotsVertical,
  IconLink,
  IconShare2,
  IconShareOff,
  IconSquarePlus2,
  IconTrash,
} from "@tabler/icons-react";
import { DateTime } from "luxon";
import { useCallback, type FC } from "react";
import { MenuTrigger } from "react-aria-components";
import { useUpdateMastraThreadMutation } from "../../api/entities/mastraThread.js";
import { appPaths } from "../../app/appPaths.js";
import {
  checkIsThreadSharedWithCompany,
  RESEARCH_THREAD_ID_PARAM,
  type CopilotChat,
  type ResearchAgentThreadMetadata,
} from "./copilotContext.js";

export interface ResearchAgentChatMenuProps {
  copilotChat: CopilotChat;
  /** The current thread's metadata (freshest available share state). */
  threadMetadata: ResearchAgentThreadMetadata;
  onRequestExpand?: () => void;
}

/** The chat options menu: expand, share, delete. */
export const ResearchAgentChatMenu: FC<ResearchAgentChatMenuProps> = (
  props,
) => {
  const { copilotChat, threadMetadata, onRequestExpand } = props;
  const { threadId, resourceId, threadResourceId } = copilotChat;
  const isOwnThread = threadResourceId === resourceId;
  const isThreadActionAvailable = threadId !== undefined && isOwnThread;
  const isShared = checkIsThreadSharedWithCompany(threadMetadata);

  const [updateThread] = useUpdateMastraThreadMutation();

  const buildShareUrl = useCallback(() => {
    if (!threadId) {
      return undefined;
    }
    // A company-scoped thread has no home page, so its link opens from the
    // app root.
    const url = new URL(appPaths.home.build(), window.location.origin);
    url.searchParams.set(RESEARCH_THREAD_ID_PARAM, threadId);
    return url.toString();
  }, [threadId]);

  const handleShareChat = useCallback(async () => {
    const shareUrl = buildShareUrl();
    if (!threadId || !shareUrl) {
      return;
    }

    // Copy first, while the user gesture's transient activation is still
    // valid — Safari rejects clipboard writes after an awaited network call.
    await navigator.clipboard.writeText(shareUrl);
    await updateThread({
      agentId: COPILOT_ID,
      threadId,
      metadata: {
        shared: [{ with: "company" as const, at: DateTime.now().toISO() }],
      },
    }).unwrap();
  }, [buildShareUrl, threadId, updateThread]);

  const handleCopyShareLink = useCallback(async () => {
    const shareUrl = buildShareUrl();
    if (shareUrl) {
      await navigator.clipboard.writeText(shareUrl);
    }
  }, [buildShareUrl]);

  const handleUnshareChat = useCallback(async () => {
    if (!threadId) {
      return;
    }

    await updateThread({
      agentId: COPILOT_ID,
      threadId,
      metadata: { shared: [] },
    }).unwrap();
  }, [threadId, updateThread]);

  const handleArchiveChat = useCallback(async () => {
    if (!threadId) {
      return;
    }

    await updateThread({
      agentId: COPILOT_ID,
      threadId,
      metadata: { archived: true },
    }).unwrap();
    copilotChat.onNewThreadSelect();
  }, [threadId, updateThread, copilotChat]);

  if (onRequestExpand === undefined && !isThreadActionAvailable) {
    return null;
  }

  return (
    <MenuTrigger>
      <IconButton
        accessibleLabel="Chat options"
        className="hover:bg-muted"
        variant={ButtonVariant.Tertiary}
        icon={<IconDotsVertical className={chatHeaderIconClassName} />}
      />
      <SimpleMenuPopover placement="bottom end">
        <SimpleMenu aria-label="Chat options">
          {onRequestExpand ? (
            <ResearchAgentMenuItem
              icon={IconSquarePlus2}
              label="Expand chat"
              onAction={onRequestExpand}
            />
          ) : null}
          {isThreadActionAvailable ? (
            <>
              {isShared ? (
                <>
                  <ResearchAgentMenuItem
                    icon={IconLink}
                    label="Copy share link"
                    onAction={() => void handleCopyShareLink()}
                  />
                  <ResearchAgentMenuItem
                    icon={IconShareOff}
                    label="Unshare"
                    onAction={() => void handleUnshareChat()}
                  />
                </>
              ) : (
                <ResearchAgentMenuItem
                  icon={IconShare2}
                  label="Share & copy link"
                  onAction={() => void handleShareChat()}
                />
              )}
              <ResearchAgentMenuItem
                icon={IconTrash}
                label="Delete"
                onAction={() => void handleArchiveChat()}
              />
            </>
          ) : null}
        </SimpleMenu>
      </SimpleMenuPopover>
    </MenuTrigger>
  );
};

interface ResearchAgentMenuItemProps {
  icon: FC<{ className?: string }>;
  label: string;
  onAction: () => void;
}

export const ResearchAgentMenuItem: FC<ResearchAgentMenuItemProps> = ({
  icon: Icon,
  label,
  onAction,
}) => (
  <SimpleMenuItem textValue={label} onAction={onAction}>
    <Icon className="size-4 text-muted-foreground" />
    <span className="min-w-0 flex-1 truncate">{label}</span>
  </SimpleMenuItem>
);
